/**
 * Dog.ts — The dog and its behaviour state machine.
 *
 * The dog has a mind of its own. A timer flips it between CALM and DISTRACTED;
 * a nearby critter forces a HYPER_PULL; and once per walk it needs to go.
 * Whatever state it's in decides where it *wants* to go and how fast — and that
 * desired velocity, transmitted through the leash spring, is what tugs the player.
 *
 * ## The lunge is a dial, not a switch
 *
 * HYPER_PULL carries a strength with it: the `pull` (0..1) the HazardManager
 * measures from how close the critter is. It sets how hard the dog hauls *and*
 * how fast it barks, so the same proximity the player can hear is the one they
 * feel in the lead. See systems/frenzy.ts, which owns both curves — and the
 * meter they also feed, which ends the run if it fills.
 *
 * ## The dog only goes on grass
 *
 * Which splits the old single poop state in two. The timer produces
 * `NEEDS_GRASS` — a dog that is *determined*, hauling you toward the nearest
 * park — and only standing properly on a green produces `POOP_PENDING`. So the
 * urge is something you have to steer, using the same lead you spend the rest
 * of the walk resisting, rather than an event that happens to you. Where the
 * grass is and what counts as being on it live in `systems/grass.ts`.
 *
 * ## The urge is announced before it lands
 *
 * `DOG_STATE.SNIFF_WARNING_MS` before the timer runs out the dog drops into
 * `SNIFFING`: it slows to a dawdle, puts its nose down, and casts about. That
 * is the player's cue to start heading for a park, and it is the whole reason
 * the state exists — steering a dog that already needs to go is a scramble, and
 * without a warning the scramble is the only version of the errand there is.
 * The warning changes nothing about *when* the dog goes.
 *
 * The state machine and its tuning are unchanged from the 2D version; only the
 * rendering (a 3D rig instead of a sprite) differs.
 */
import { DOG, DOG_STATE, ANIM, EFFECTS } from '../config/constants';
import { activeLevel } from '../config/level';
import { GRASS_MARGIN, nearestGrass, onGrass } from '../systems/grass';
import { barkIntervalMs } from '../systems/frenzy';
import { DogState } from '../types';
import { Entity } from './Entity';
import { dogSpeedForState } from './dogBehaviour';
import { dogPoseForState, blendPose, type DogPose } from './dogPose';
import { isMoving } from './locomotion';
import {
  createDog,
  animateWalk,
  animateTail,
  applyPose,
  type CharacterRig,
} from '../render/models';
import { smoothing } from '../utils/math';
import { vec2, type Vec2 } from '../utils/vec2';

export class Dog extends Entity {
  private rig: CharacterRig;
  private _state: DogState = DogState.CALM;
  /** Where the dog currently wants to move. */
  private target = vec2();

  // State timers (ms), counting down.
  private calmTimer = 0;
  private distractedTimer = 0;
  private retargetTimer = 0;
  private poopTimer: number;
  private hasPooped = false;
  /** Milliseconds left of the squat itself, once the dog is on the grass. */
  private poopingMs = 0;
  /** Milliseconds until the next bark while lunging. */
  private barkTimer = 0;
  /** How hard the current critter is pulling (0..1); only meaningful in HYPER_PULL. */
  private pull = 0;
  /** Current (eased) body pose; blended toward the state's target each frame. */
  private pose: DogPose = dogPoseForState(DogState.CALM);

  /** Optional hook so the game core / HUD learn about state changes. */
  onStateChange?: (state: DogState) => void;
  /** Fired every time the dog barks, with the strength of the lunge behind it. */
  onBark?: (intensity: number) => void;

  constructor(
    x: number,
    y: number,
    private player: Entity,
  ) {
    const rig = createDog();
    super(x, y, DOG.RADIUS, rig.group);
    this.rig = rig;

    // Schedule the one-and-only poop for this walk.
    this.poopTimer = DOG_STATE.POOP_MIN_MS + Math.random() * DOG_STATE.POOP_JITTER_MS;
    this.enterCalm();
  }

  get dogState(): DogState {
    return this._state;
  }

  // ---- External state control (called by HazardManager / the game core) -------

  /**
   * Lock the dog onto a hazard position (a critter came too close).
   *
   * `pull` is how strong the lunge is, 0..1 — see systems/frenzy.ts. It's
   * refreshed every frame along with the target, because the critter is moving
   * and so is the dog, so the strength has to track the gap rather than being
   * latched at the moment of the spot. A fresh lunge barks immediately: the
   * bark *is* the notification that something has caught the dog's eye.
   */
  enterHyperPull(targetX: number, targetY: number, pull = 1): void {
    this.target.x = targetX;
    this.target.y = targetY;
    this.pull = pull;
    if (this._state !== DogState.HYPER_PULL) {
      this.setState_(DogState.HYPER_PULL);
      this.barkTimer = 0; // bark on the spot, then settle into the interval
    }
  }

  /** How hard the dog is currently lunging, 0..1. Zero unless it's in HYPER_PULL. */
  get pullStrength(): number {
    return this._state === DogState.HYPER_PULL ? this.pull : 0;
  }

  /** How far through the squat the dog is, 0..1. Zero unless it's going. */
  get poopProgress(): number {
    if (this._state !== DogState.POOPING) return 0;
    const done = DOG_STATE.POOP_DURATION_MS - this.poopingMs;
    return Math.min(1, Math.max(0, done / DOG_STATE.POOP_DURATION_MS));
  }

  /**
   * Hazard left range — relax back to a calm wander (unless mid-poop).
   *
   * A dog that still needs to go goes back to *that* rather than to calm. A cat
   * can interrupt the walk to the park, but it can't make the dog forget: the
   * urge is the level's first objective, so losing it to a passing squirrel
   * would strand the player. (The timer below would re-fire it on the next
   * frame anyway — this is what stops the HUD flickering through `Calm` on the
   * way.)
   */
  releaseHyperPull(): void {
    if (this._state !== DogState.HYPER_PULL) return;
    this.pull = 0;
    if (this.needsGrass()) this.setState_(DogState.NEEDS_GRASS);
    else if (this.urgeIsClose()) this.enterSniffing();
    else this.enterCalm();
  }

  /** Whether the urge has arrived and hasn't been satisfied yet. */
  private needsGrass(): boolean {
    return !this.hasPooped && this.poopTimer <= 0;
  }

  /** Whether the urge is due within the warning window — time to start sniffing. */
  private urgeIsClose(): boolean {
    return !this.hasPooped && this.poopTimer <= DOG_STATE.SNIFF_WARNING_MS;
  }

  /**
   * Whether the warning may fire from the state the dog is in now.
   *
   * A distraction is cut short for it, deliberately: the window is only a few
   * seconds long, and one bolt after a pigeon at the wrong moment would eat the
   * whole of it and hand the player the old no-warning poop back. A dog already
   * sniffing, going, or locked onto a critter is left alone.
   */
  private canStartSniffing(): boolean {
    return this._state === DogState.CALM || this._state === DogState.DISTRACTED;
  }

  /**
   * Whether the dog is after a park: nosing about for one, or hauling you to it
   * outright.
   *
   * Read by the game core, which puts a ring on the nearest green for exactly
   * as long as this is true. "Somewhere on the grass" is not a thing the player
   * can see from the dog's body language, however good the pose is — the town
   * has several parks and most of them are behind a building.
   *
   * Asked of the *need* rather than the current state, so a cat crossing the
   * road mid-errand doesn't blink the ring off and on again: the dog can be
   * yanked into a hyper pull on the way to the park without the errand going
   * anywhere. It ends when the dog goes, not when it stops walking there.
   */
  get seekingGrass(): boolean {
    return this.urgeIsClose();
  }

  /** Called once the player finishes cleaning up. */
  markCleaned(): void {
    this.setState_(DogState.CLEANED);
    this.enterCalm(DogState.CLEANED); // resume calm wandering, keep the CLEANED label
  }

  /**
   * Start going, here and now.
   *
   * The act takes `DOG_STATE.POOP_DURATION_MS`: this enters POOPING and roots
   * the dog, and `update` flips it to POOP_PENDING — the state the rest of the
   * game watches for — once the squat is finished.
   *
   * **Bypasses the grass rule on purpose** — this is the hook tests, the
   * browser smoke-run and the dev console use to skip the walk to the park.
   * Normal play never calls it directly; it arrives via `NEEDS_GRASS` once the
   * dog is actually standing on a green (see `update`).
   */
  enterPoop(): void {
    this.hasPooped = true;
    this.poopingMs = DOG_STATE.POOP_DURATION_MS;
    this.setState_(DogState.POOPING);
    this.stop();
  }

  /**
   * Bring the urge forward to now — without granting it.
   *
   * The counterpart to `enterPoop`, and the one that **keeps** the grass rule:
   * the dog starts looking for a park on the next frame and goes once it gets
   * there under its own steam. The browser smoke-run forces the urge this way so
   * it plays the walk to the grass for real, which is the only place that rule
   * is exercised against a live level rather than a fixture.
   */
  feelTheUrge(): void {
    if (this.hasPooped) return;
    this.poopTimer = 0;
  }

  /**
   * Stop the urge timer running, without resetting it.
   *
   * Set by the guided walk while it is still explaining the lead, so the dog
   * doesn't need the grass halfway through a sentence about it — the urge lands
   * on its own schedule everywhere else, and a level that is teaching the game
   * is the one place worth waiting for the player. Cleared again the moment the
   * lesson about the urge comes up. See `systems/tutorial.ts` → `holdsUrge`.
   */
  urgeHeld = false;

  /**
   * Bring the urge forward to *at most* `ms` away.
   *
   * Between `feelTheUrge` (now, no warning) and leaving the timer alone: the
   * guided walk uses it to make the lesson about the urge pay off in a couple of
   * seconds rather than the eight-to-seventeen the schedule would give. Never
   * pushes the urge back — a dog already about to go stays about to go.
   */
  hurryUrge(ms: number): void {
    if (this.hasPooped) return;
    this.poopTimer = Math.min(this.poopTimer, ms);
  }

  /** Can the dog currently be pulled by hazards / distractions? */
  get isBusy(): boolean {
    return this._state === DogState.POOPING || this._state === DogState.POOP_PENDING;
  }

  // ---- Internal state helpers -------------------------------------------------

  private setState_(next: DogState): void {
    if (this._state === next) return;
    this._state = next;
    this.onStateChange?.(next);
  }

  private enterCalm(label: DogState = DogState.CALM): void {
    this.setState_(label);
    this.calmTimer = DOG_STATE.CALM_MIN_MS + Math.random() * DOG_STATE.CALM_JITTER_MS;
    this.retargetTimer = 0; // force an immediate calm retarget
  }

  /**
   * Start nosing about for a spot. Keeps the calm wander's targeting — the dog
   * is still pottering around the walker — but at a dawdle, and it can no
   * longer be pulled off into a distraction; a dog this close to needing to go
   * has one thing on its mind.
   */
  private enterSniffing(): void {
    this.setState_(DogState.SNIFFING);
    this.retargetTimer = 0; // pick somewhere to sniff on the next frame
  }

  private enterDistracted(): void {
    this.setState_(DogState.DISTRACTED);
    this.distractedTimer = DOG_STATE.DISTRACTED_MS;
    // Pick a random point to bolt toward within a radius of the dog.
    const angle = Math.random() * Math.PI * 2;
    const dist = DOG_STATE.DISTRACT_TARGET_RADIUS * (0.4 + Math.random() * 0.6);
    this.target.x = this.pos.x + Math.cos(angle) * dist;
    this.target.y = this.pos.y + Math.sin(angle) * dist;
  }

  // ---- Per-frame update -------------------------------------------------------

  /** Advance the state machine. `deltaMs` is the frame delta in milliseconds. */
  update(deltaMs: number): void {
    // The urge takes priority: it arrives once per walk, from any non-hyper
    // state. What it produces is a dog that *needs* to go, not one that has —
    // the going itself only happens on grass, below.
    if (!this.hasPooped && this._state !== DogState.HYPER_PULL) {
      // A hold stops the *clock*, and only the clock. An urge that is already
      // due still lands — which is what keeps `feelTheUrge` working on a guided
      // walk, where the hold is on for the whole of the opening.
      if (!this.urgeHeld) this.poopTimer -= deltaMs;
      if (this.poopTimer <= 0) {
        this.setState_(DogState.NEEDS_GRASS);
      } else if (this.poopTimer <= DOG_STATE.SNIFF_WARNING_MS && this.canStartSniffing()) {
        this.enterSniffing();
      }
    }

    switch (this._state) {
      case DogState.CALM:
      case DogState.SNIFFING:
      case DogState.CLEANED: {
        // Gentle wander: periodically pick a spot near the player and drift to it.
        // The spot is drawn from a *ring*, not a disc — the inner radius keeps the
        // dog from targeting the walker's own feet, which with body collision on
        // would just have it shouldering them for the whole walk.
        this.retargetTimer -= deltaMs;
        if (this.retargetTimer <= 0) {
          this.retargetTimer = 600 + Math.random() * 900;
          const angle = Math.random() * Math.PI * 2;
          const r =
            DOG_STATE.WANDER_MIN_RADIUS +
            Math.random() * (DOG_STATE.WANDER_MAX_RADIUS - DOG_STATE.WANDER_MIN_RADIUS);
          this.target.x = this.player.pos.x + Math.cos(angle) * r;
          this.target.y = this.player.pos.y + Math.sin(angle) * r;
        }
        this.calmTimer -= deltaMs;
        // Only a normal calm dog escalates to DISTRACTED; a CLEANED dog stays chill.
        if (this._state === DogState.CALM && this.calmTimer <= 0) this.enterDistracted();
        break;
      }
      case DogState.DISTRACTED: {
        this.distractedTimer -= deltaMs;
        if (this.distractedTimer <= 0) this.enterCalm();
        break;
      }
      case DogState.HYPER_PULL:
        // Target and pull strength are refreshed externally by HazardManager
        // each frame; all this state owns is the barking.
        this.tickBark(deltaMs);
        break;
      case DogState.NEEDS_GRASS: {
        // Heads for the nearest park and goes the moment it is properly on it.
        // The target is re-read every frame rather than latched: the walker is
        // dragging the dog around by the lead the whole time, so the nearest
        // grass can genuinely change on the way.
        const parks = activeLevel().parks;
        if (onGrass(this.pos.x, this.pos.y, parks, GRASS_MARGIN)) {
          this.enterPoop();
          break;
        }
        const spot = nearestGrass(this.pos, parks, GRASS_MARGIN);
        // No grass at all is a level the validator refuses to ship. If one ever
        // reached a player, the dog would simply keep needing to go rather than
        // freezing or throwing.
        if (spot) {
          this.target.x = spot.x;
          this.target.y = spot.y;
        }
        break;
      }
      case DogState.POOPING: {
        // Mid-squat: rooted, and counting down to the poop actually landing.
        this.stop();
        this.poopingMs -= deltaMs;
        if (this.poopingMs <= 0) {
          this.poopingMs = 0;
          this.setState_(DogState.POOP_PENDING);
        }
        break;
      }
      case DogState.POOP_PENDING:
        this.stop(); // rooted until cleaned up
        break;
    }
  }

  /**
   * Bark while lunging, at a rate set by how close the critter is.
   *
   * The interval is recomputed from the *current* pull every time one fires, so
   * a dog being dragged away from a cat audibly winds down rather than keeping
   * the rate it started at.
   */
  private tickBark(deltaMs: number): void {
    this.barkTimer -= deltaMs;
    if (this.barkTimer > 0) return;
    this.barkTimer = barkIntervalMs(this.pull, DOG.BARK_MIN_MS, DOG.BARK_MAX_MS);
    this.onBark?.(this.pull);
  }

  /**
   * Desired velocity (units/s) toward the current target, magnitude set by state.
   * The Leash system reads this and blends in the spring tension.
   */
  getDesiredVelocity(out: Vec2): Vec2 {
    if (this.isBusy) {
      out.x = 0;
      out.y = 0;
      return out;
    }

    out.x = this.target.x - this.pos.x;
    out.y = this.target.y - this.pos.y;
    const distSq = out.x * out.x + out.y * out.y;
    if (distSq < 36) {
      out.x = 0;
      out.y = 0;
      return out; // arrived — avoid jitter
    }

    const dist = Math.sqrt(distSq);
    // The lunge is scaled by how close the critter is; every other state
    // ignores the argument.
    const speed = dogSpeedForState(this._state, this.pull);
    out.x = (out.x / dist) * speed;
    out.y = (out.y / dist) * speed;
    return out;
  }

  /**
   * Drive the trot cycle, mood pose, tail wag, and facing. `t` is elapsed seconds.
   *
   * The pose is *eased* toward the current state's target rather than snapped, so
   * the dog visibly tenses into a hyper-pull and relaxes out of it.
   */
  updateVisuals(dt: number, t: number): void {
    this.advanceAnimation(dt);
    animateWalk(
      this.rig,
      this.walkPhase,
      isMoving(this.vel.x, this.vel.y),
      ANIM.DOG_LEG_SWING,
      ANIM.DOG_BOB,
    );

    this.pose = blendPose(
      this.pose,
      dogPoseForState(this._state),
      smoothing(EFFECTS.POSE_LERP, dt),
    );
    applyPose(this.rig, this.pose);
    animateTail(this.rig, t, this.pose.wagSpeed, this.pose.wagAmount);
    this.syncMesh();
  }
}
